/**
 * 이상한 방 - 색 팔레트 (따뜻한 파스텔 실내)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4절(이산값 원칙) · "팔레트 설계 요건"
 *
 * 이 파일이 존재하는 이유는 하나다: **색 변경은 연속값이 아니라 인덱스여야 한다.**
 * RGB 자유 선택을 허용하면 체인저가 "미묘하게 다른 회색"을 만들 수 있고,
 * 그건 난이도가 아니라 사기다(탄환 경제 하에서 억울한 탈락으로 직결).
 *
 * ## 설계 요건 두 가지 (4절)
 *
 * 1. 8~12색 수준 - 여기서는 10색. 인덱스는 직렬화되는 값이므로 **개수와 순서가 계약**이다.
 * 2. **색상뿐 아니라 명도까지 벌린다.** 색약 접근성이 부수적으로 확보된다.
 *    지금 지키면 비용 0, 카탈로그 확정 후 고치면 전량 재작업이므로 지금 지킨다.
 *
 * ## 명도를 "어떻게" 벌렸는가 - 균일 명암비 사다리
 *
 * 상대 휘도(Y)를 등간격으로 벌리면 안 된다. 사람 눈의 명도 반응은 비선형이라
 * 어두운 쪽 두 색은 구별이 안 되고 밝은 쪽만 과하게 벌어진다.
 * 그래서 **인접 색의 명암비 (Y₁+0.05)/(Y₂+0.05) 가 일정하도록** 역산했다.
 *
 *   Yᵢ + 0.05 = 0.07 × 1.3333ⁱ   (i = 0..9)
 *
 * 결과: 인접 색 명암비가 전부 약 1.33:1로 균일하고, 최저색↔최고색 명암비는 13.3:1이다.
 * 10색을 한 축에 세우는 이상 1.33:1이 사실상 상한이다 -
 * 인접 1.5:1을 요구하면 1.5⁹ = 38배가 필요한데 sRGB에서 표현 불가능하다.
 *
 * 부수 효과: 채널 정보를 전부 버리고 회색조로 변환해도(= 완전 색맹 시뮬레이션)
 * 열 개 색이 여전히 서로 다른 회색으로 남는다. 이것이 접근성이 "부수적으로" 확보된다는 말의 실체다.
 *
 * ## 왜 색상은 따뜻한 실내 톤인데 채도는 낮은가
 *
 * 목표 룩은 카툰 로우폴리 + 파스텔 실내다. 그래서 **색상(hue)은 실내 팔레트에서 고른다** -
 * 코코아·자두·세이지·로즈·크림. 앞선 회색 박스 단계의 무채색 조합을 걷어냈다.
 *
 * 다만 **채도를 올려 파스텔을 흉내 내지는 않는다.** 이 게임의 화면은 장식이 아니라 판정 근거이고,
 * 채도가 높은 색끼리는 조명·모니터 색보정에 따라 서로 밀려 보인다. 4절이 "명도까지 벌린다"고
 * 못박은 이유가 그것이다. 그래서 사다리(명도)는 그대로 두고 색상만 갈아 끼웠다 -
 * 파스텔의 인상은 **밝은 쪽 색(7~9)의 넓은 면적**이 만들고, 구별은 여전히 명도가 담당한다.
 *
 * 아래 `luminance` 값은 전부 이 파일의 `relativeLuminance()`로 실제 계산한 값이며,
 * 손으로 적은 값이 hex와 어긋나는 것을 막기 위해 테스트가 둘의 일치를 강제한다.
 */

import type { PaletteIndex } from '../core/types'

/** 팔레트 한 칸. `colorId`가 가리키는 대상이며 이 배열 밖의 색은 게임에 존재하지 않는다. */
export type PaletteColor = Readonly<{
  /** 배열 인덱스와 동일. 직렬화되는 유일한 색 표현이다. */
  id: PaletteIndex
  name: string
  /** 0xRRGGBB. Three.js `Color`가 그대로 받는 형식이라 렌더 레이어에서 변환이 필요 없다. */
  hex: number
  /** WCAG 상대 휘도(0~1). hex에서 계산된 파생값이며 캐시 목적으로만 들고 있다. */
  luminance: number
}>

/* --- sRGB → 상대 휘도 변환 상수 (WCAG 2.x 정의) -------------------------------- */

const SRGB_LINEAR_CUTOFF = 0.04045
const SRGB_LOW_SLOPE = 12.92
const SRGB_ALPHA = 0.055
const SRGB_GAMMA = 2.4

const LUMINANCE_WEIGHT_RED = 0.2126
const LUMINANCE_WEIGHT_GREEN = 0.7152
const LUMINANCE_WEIGHT_BLUE = 0.0722

/** WCAG 명암비 공식의 상수항. 완전 검정 두 개의 명암비가 0/0이 되는 것을 막는다. */
const CONTRAST_OFFSET = 0.05

const BYTE_MASK = 0xff
const RED_SHIFT = 16
const GREEN_SHIFT = 8
const MAX_CHANNEL_VALUE = 255

/**
 * 인접 팔레트 색이 지켜야 할 최소 상대 휘도 차.
 * 실제 사다리의 최소 간격은 0.0228(0↔1 구간)이므로 여유를 두고 0.02로 잡는다.
 * 이 값보다 촘촘해지면 "색을 바꿨는데 안 보인다" = 원칙 2 위반이 된다.
 */
export const MIN_ADJACENT_LUMINANCE_GAP = 0.02

/**
 * 인접 팔레트 색이 지켜야 할 최소 명암비.
 * 휘도 차(위)는 어두운 쪽에서 관대해지므로, 지각적 기준인 명암비로 한 번 더 조인다.
 * 실제 사다리는 전 구간 약 1.33:1이며 여유를 두고 1.25로 잡는다.
 */
export const MIN_ADJACENT_CONTRAST_RATIO = 1.25

/**
 * 10색 팔레트. **휘도 오름차순으로 정렬돼 있다** - 이 순서 자체가 계약이다.
 * 인접 검증(테스트)이 배열 순서에 의존하므로 중간 삽입 시 사다리를 다시 계산해야 한다.
 *
 * | id | 이름       | hex     | 휘도 Y | 인접 명암비 | 색상          | 실내 역할            |
 * |----|------------|---------|--------|-------------|---------------|---------------------|
 * |  0 | 코코아     | #32241B | 0.0202 |      -      | 갈색 22°      | 짙은 원목·가죽       |
 * |  1 | 자두       | #542F41 | 0.0430 |    1.33     | 자주 332°     | 짙은 패브릭          |
 * |  2 | 먹청록     | #31525C | 0.0746 |    1.34     | 청록 194°     | 페인트 칠한 가구      |
 * |  3 | 짙은세이지 | #46683D | 0.1154 |    1.33     | 초록 108°     | 화분·식물            |
 * |  4 | 라일락     | #966096 | 0.1705 |    1.33     | 보라 300°     | 쿠션·소품 포인트      |
 * |  5 | 카페라떼   | #A38163 | 0.2439 |    1.33     | 갈색 28°      | 기본 원목            |
 * |  6 | 세이지     | #87A871 | 0.3435 |    1.34     | 초록 96°      | 잎·러그              |
 * |  7 | 로즈       | #D1B0AA | 0.4751 |    1.33     | 붉은 8°       | 파스텔 핑크 패브릭    |
 * |  8 | 살구크림   | #DED1BF | 0.6489 |    1.33     | 주황 34°      | 벽·큰 면적           |
 * |  9 | 우유빛     | #F4F1EA | 0.8808 |    1.33     | 노랑 44°      | 도자기·종이·리넨      |
 *
 * 색상은 갈색 → 자주 → 청록 → 초록 → 보라 → 붉은 → 주황으로 흩어 두어,
 * 명도가 비슷하게 보이는 조명 아래에서도 색상만으로 한 번 더 구별되게 했다.
 */
export const PALETTE: readonly PaletteColor[] = [
  { id: 0, name: '코코아', hex: 0x32241b, luminance: 0.0202 },
  { id: 1, name: '자두', hex: 0x542f41, luminance: 0.043 },
  { id: 2, name: '먹청록', hex: 0x31525c, luminance: 0.0746 },
  { id: 3, name: '짙은세이지', hex: 0x46683d, luminance: 0.1154 },
  { id: 4, name: '라일락', hex: 0x966096, luminance: 0.1705 },
  { id: 5, name: '카페라떼', hex: 0xa38163, luminance: 0.2439 },
  { id: 6, name: '세이지', hex: 0x87a871, luminance: 0.3435 },
  { id: 7, name: '로즈', hex: 0xd1b0aa, luminance: 0.4751 },
  { id: 8, name: '살구크림', hex: 0xded1bf, luminance: 0.6489 },
  { id: 9, name: '우유빛', hex: 0xf4f1ea, luminance: 0.8808 },
]

export const PALETTE_SIZE = PALETTE.length

/**
 * 팔레트 색 조회. 범위를 벗어나면 **던진다.**
 *
 * `undefined`를 돌려주지 않는 이유: `colorId`는 서버가 검증한 상태에서만 오므로
 * 여기서 벗어난 값은 데이터 오류가 아니라 프로그래밍 오류다.
 * 조용히 기본색으로 대체하면 "색이 안 바뀌었는데 서버 diff에는 변화로 잡히는" 최악의 버그가 되고,
 * 이는 술래가 찾을 수 없는 변화 = 원칙 2 위반이다. 시끄럽게 실패하는 편이 낫다.
 *
 * 외부에서 들어온 값(네트워크 페이로드 등)은 `isPaletteIndex()`로 경계에서 먼저 거른다.
 */
export function getColor(id: PaletteIndex): PaletteColor {
  const color = PALETTE[id]
  if (color === undefined) {
    throw new RangeError(
      `팔레트 범위를 벗어난 colorId입니다: ${String(id)} (유효 범위 0~${PALETTE_SIZE - 1})`,
    )
  }
  return color
}

/** 시스템 경계(역직렬화·사용자 입력)에서 쓰는 검증기. 정수이며 팔레트 범위 안인지 본다. */
export function isPaletteIndex(value: unknown): value is PaletteIndex {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < PALETTE_SIZE
}

/** sRGB 채널값(0~1)을 선형 광량으로 되돌린다. 감마 보정의 역함수. */
function toLinearChannel(channel: number): number {
  if (channel <= SRGB_LINEAR_CUTOFF) return channel / SRGB_LOW_SLOPE
  return Math.pow((channel + SRGB_ALPHA) / (1 + SRGB_ALPHA), SRGB_GAMMA)
}

/**
 * 0xRRGGBB의 WCAG 상대 휘도(0~1)를 계산한다.
 * 팔레트의 `luminance` 필드는 이 함수의 결과를 적어 둔 것이며,
 * 테스트가 매번 재계산해 둘이 어긋나지 않았는지 검사한다.
 */
export function relativeLuminance(hex: number): number {
  const red = ((hex >> RED_SHIFT) & BYTE_MASK) / MAX_CHANNEL_VALUE
  const green = ((hex >> GREEN_SHIFT) & BYTE_MASK) / MAX_CHANNEL_VALUE
  const blue = (hex & BYTE_MASK) / MAX_CHANNEL_VALUE
  return (
    LUMINANCE_WEIGHT_RED * toLinearChannel(red) +
    LUMINANCE_WEIGHT_GREEN * toLinearChannel(green) +
    LUMINANCE_WEIGHT_BLUE * toLinearChannel(blue)
  )
}

/** 두 휘도의 WCAG 명암비(1.0 ~ 21.0). 인수 순서는 상관없다. */
export function contrastRatio(luminanceA: number, luminanceB: number): number {
  const brighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)
  return (brighter + CONTRAST_OFFSET) / (darker + CONTRAST_OFFSET)
}
